var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/detail-page.tsx
import * as React2 from "react";

// src/detail-tabs.tsx
import { cn } from "@repo/utils";
function DetailTabs({ tabs, activeTab, onChange }) {
  if (tabs.length <= 1) return null;
  return /* @__PURE__ */ React.createElement("div", {
    className: "border-b border-border mb-6"
  }, /* @__PURE__ */ React.createElement("div", {
    className: "flex space-x-2 overflow-x-auto -mb-px"
  }, tabs.map((tab) => /* @__PURE__ */ React.createElement("button", {
    key: tab.id,
    type: "button",
    onClick: /* @__PURE__ */ __name(() => onChange(tab.id), "onClick"),
    className: cn("pt-2 pb-3 px-4 border-b-2 text-sm font-medium transition-all rounded-none bg-transparent hover:bg-transparent whitespace-nowrap", activeTab === tab.id ? "border-accent-500 text-accent-500 font-semibold" : "border-transparent text-text-secondary hover:text-text-primary hover:border-border-strong")
  }, tab.label))));
}
__name(DetailTabs, "DetailTabs");

// src/detail-page.tsx
import { cn as cn2 } from "@repo/utils";
function PageSkeleton() {
  return /* @__PURE__ */ React2.createElement("div", {
    className: "flex-1 flex flex-col gap-6 p-6 md:p-8 animate-in fade-in duration-500"
  }, /* @__PURE__ */ React2.createElement("div", {
    className: "flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
  }, /* @__PURE__ */ React2.createElement("div", {
    className: "flex flex-col gap-2 w-full max-w-xs"
  }, /* @__PURE__ */ React2.createElement("div", {
    className: "h-8 w-3/4 bg-surface-3 rounded animate-pulse"
  }), /* @__PURE__ */ React2.createElement("div", {
    className: "h-4 w-1/2 bg-surface-3 rounded animate-pulse"
  }))), /* @__PURE__ */ React2.createElement("div", {
    className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
  }, /* @__PURE__ */ React2.createElement("div", {
    className: "h-28 bg-surface-3 rounded-md animate-pulse"
  }), /* @__PURE__ */ React2.createElement("div", {
    className: "h-28 bg-surface-3 rounded-md animate-pulse"
  }), /* @__PURE__ */ React2.createElement("div", {
    className: "h-28 bg-surface-3 rounded-md animate-pulse"
  }), /* @__PURE__ */ React2.createElement("div", {
    className: "h-28 bg-surface-3 rounded-md animate-pulse"
  })), /* @__PURE__ */ React2.createElement("div", {
    className: "border border-border rounded-lg bg-surface-1 overflow-hidden"
  }, /* @__PURE__ */ React2.createElement("div", {
    className: "p-4 border-b border-border"
  }, /* @__PURE__ */ React2.createElement("div", {
    className: "h-6 w-48 bg-surface-3 rounded animate-pulse"
  })), /* @__PURE__ */ React2.createElement("div", {
    className: "p-4 space-y-3"
  }, Array.from({
    length: 5
  }).map((_, i) => /* @__PURE__ */ React2.createElement("div", {
    key: i,
    className: "h-4 bg-surface-3 rounded animate-pulse"
  })))));
}
__name(PageSkeleton, "PageSkeleton");
function DefaultError({ error, retry }) {
  return /* @__PURE__ */ React2.createElement("div", {
    className: "flex flex-col items-center justify-center gap-3 p-8 text-center bg-surface-1 rounded-md border border-border"
  }, /* @__PURE__ */ React2.createElement("p", {
    className: "text-sm font-semibold text-danger-500"
  }, "Error"), /* @__PURE__ */ React2.createElement("p", {
    className: "text-xs text-text-muted max-w-xs"
  }, error?.message || "Something went wrong"), retry && /* @__PURE__ */ React2.createElement("button", {
    type: "button",
    onClick: retry,
    className: "px-4 py-2 text-sm font-medium bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors mt-2"
  }, "Try Again"));
}
__name(DefaultError, "DefaultError");
function DetailPage({ config, detail, mainPane, asidePane, headerActions, backButton, statusBadge, title, subtitle, loadingSkeleton, errorComponent }) {
  if (detail.isLoading) {
    return loadingSkeleton || /* @__PURE__ */ React2.createElement(PageSkeleton, null);
  }
  if (detail.isError) {
    return errorComponent || /* @__PURE__ */ React2.createElement(DefaultError, {
      error: detail.error,
      retry: detail.refetch
    });
  }
  if (!detail.data) {
    return null;
  }
  return /* @__PURE__ */ React2.createElement("div", {
    style: {
      viewTransitionName: "page-body"
    },
    className: "max-w-[1200px] mx-auto w-full"
  }, /* @__PURE__ */ React2.createElement("div", {
    className: cn2("flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5")
  }, /* @__PURE__ */ React2.createElement("div", {
    className: "flex flex-col gap-1.5 min-w-0"
  }, backButton && /* @__PURE__ */ React2.createElement("div", {
    className: "-mt-1 mb-1"
  }, backButton), /* @__PURE__ */ React2.createElement("h1", {
    className: "font-newsreader font-semibold text-2xl sm:text-3xl leading-tight text-text-primary truncate"
  }, title || detail.pageTitle), subtitle && /* @__PURE__ */ React2.createElement("div", {
    className: "text-sm text-text-secondary"
  }, subtitle), statusBadge && /* @__PURE__ */ React2.createElement("div", {
    className: "flex flex-wrap items-center gap-2 mt-1.5"
  }, statusBadge)), headerActions && /* @__PURE__ */ React2.createElement("div", {
    className: "flex-shrink-0 flex items-center gap-2 pt-1"
  }, headerActions)), /* @__PURE__ */ React2.createElement(DetailTabs, {
    tabs: config.tabs,
    activeTab: detail.activeTab,
    onChange: detail.setActiveTab
  }), /* @__PURE__ */ React2.createElement("div", {
    className: cn2(asidePane ? "grid grid-cols-1 lg:grid-cols-12 gap-6" : "")
  }, asidePane && /* @__PURE__ */ React2.createElement("div", {
    className: "lg:col-span-4 lg:order-2"
  }, asidePane), /* @__PURE__ */ React2.createElement("div", {
    className: cn2(asidePane ? "lg:col-span-8 lg:order-1" : "")
  }, mainPane)));
}
__name(DetailPage, "DetailPage");

// src/inline-edit-toggle.tsx
import * as React3 from "react";
import { cn as cn3 } from "@repo/utils";
function InlineEditToggle({ isEditing, onStartEdit, onCancel, children, editChildren, className, editButtonLabel = "Edit" }) {
  if (isEditing) {
    return /* @__PURE__ */ React3.createElement("div", {
      className: cn3("space-y-4", className)
    }, editChildren, /* @__PURE__ */ React3.createElement("div", {
      className: "flex justify-end gap-2"
    }, /* @__PURE__ */ React3.createElement("button", {
      type: "button",
      onClick: onCancel,
      className: "px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
    }, "Cancel"), /* @__PURE__ */ React3.createElement("button", {
      type: "submit",
      className: "px-4 py-2 text-sm font-medium bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors"
    }, "Save Changes")));
  }
  return /* @__PURE__ */ React3.createElement("div", {
    className: cn3("space-y-4", className)
  }, children, /* @__PURE__ */ React3.createElement("button", {
    type: "button",
    onClick: onStartEdit,
    className: "text-sm font-medium text-accent-500 hover:text-accent-600 transition-colors"
  }, editButtonLabel));
}
__name(InlineEditToggle, "InlineEditToggle");
export {
  DetailPage,
  DetailTabs,
  InlineEditToggle
};
//# sourceMappingURL=index.js.map